import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchApi, getUploadsUrl } from "../../config";

const PROJECTS = [
    { href: "https://www.chasclarkson.com.au/", initials: "CC", kicker: "RETAIL & EVENTS", title: "Chas Clarkson" },
    { href: "https://www.netrefine.com/", initials: "NR", kicker: "MANAGED IT", title: "Netrefine" },
    { href: "https://roobster.io/", initials: "RB", kicker: "E-MOBILITY", title: "Roobster" },
    { href: "https://silverspoons.com/", initials: "SS", kicker: "E-COMMERCE", title: "Silver Spoons" },
    { href: "https://alignity.io/", initials: "AC", kicker: "HR TECH", title: "Alignity Consulting" },
    { href: "https://mistershopusa.com/", initials: "MS", kicker: "FULFILLMENT", title: "Mister Shop USA" },
];

export default function RecentWork() {
    const trackRef = useRef(null);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState(PROJECTS.slice(0, 10));
    const projectsRef = useRef(projects);

    useEffect(() => { projectsRef.current = projects; }, [projects]);

    const scrollToIndex = (i) => {
        const track = trackRef.current;
        if (!track) return;
        const len = projectsRef.current.length || PROJECTS.length;
        const clamped = ((i % len) + len) % len;
        const item = track.children[clamped];
        if (item) {
            track.scrollTo({ left: item.offsetLeft, behavior: "smooth" });
        }
        setActive(clamped);
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const onScroll = () => {
            const items = [...track.children];
            let closest = 0;
            let min = Infinity;
            items.forEach((item, i) => {
                const diff = Math.abs(item.offsetLeft - track.scrollLeft);
                if (diff < min) {
                    min = diff;
                    closest = i;
                }
            });
            setActive(closest);
        };
        track.addEventListener("scroll", onScroll, { passive: true });
        return () => track.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const res = await fetchApi("get_portfolio.php");
                if (!mounted) return;
                if (res && Array.isArray(res.Portfolio)) {
                    // Prefer items marked as featured === '1'
                    const featuredItems = res.Portfolio.filter(p => String(p.featured) === '1');
                    const source = featuredItems.length ? featuredItems : res.Portfolio;
                    const mapped = source.slice(0, 10).map((p) => ({
                        id: p.id,
                        href: p.site_url || p.site_url,
                        initials: (p.site_title || "").split(" ").map(s=>s[0]).slice(0,2).join('').toUpperCase(),
                        kicker: p.category_name || "",
                        title: p.site_title || p.description || "",
                        site_image: p.site_image,
                        site_image_name: p.site_image_name,
                        site_logo: p.site_logo,
                        site_logo_name: p.site_logo_name,
                    }));
                    setProjects(mapped);
                    projectsRef.current = mapped;
                    setActive(0);
                }
            } catch (err) {
                // keep defaults on error
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    return (
        <section className="section section--navy">
            <div className="container">
                <div className="center mb-44">
                    <div className="eyebrow">Recent work</div>
                    <h2 className="h-lg tx-white">Built for real businesses, in the wild.</h2>
                </div>

                <div className="mini-slider">
                    {!loading && (
                        <button aria-label="Previous" className="slider-btn prev" type="button" onClick={() => scrollToIndex(active - 1)}>
                            <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"></polyline></svg>
                        </button>
                    )}

                    <div className="mini-track work-mini" ref={trackRef} aria-busy={loading}>
                        {loading ? new Array(3).fill(0).map((_, index) => (
                            <div className="item card skeleton" key={`project-placeholder-${index}`}>
                                <div className="thumb">
                                    <div className="graphic graphic--fill">
                                        <div className="graphic-center">
                                            <span className="placeholder-box" />
                                        </div>
                                    </div>
                                </div>
                                <div className="body">
                                    <span className="placeholder-line small" />
                                    <span className="placeholder-line title" />
                                    <span className="placeholder-line" />
                                </div>
                            </div>
                        )) : projects.map((p) => (
                            <a key={p.id || p.title} className="item card" href={p.href} rel="noopener noreferrer" target="_blank">
                                <div className="thumb">
                                    <div className="graphic graphic--fill">
                                        <span className="dots"></span>
                                        <div className="graphic-center">
                                            {p.site_image ? (
                                                <img src={getUploadsUrl('portfolio', p.site_image)} alt={p.site_image_name || p.title} />
                                            ) : (
                                                <span className="initials">{p.initials}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="body">
                                    <span className="kicker">{p.kicker}</span>
                                    <h3>{p.title}</h3>
                                    <span className="go">Explore project →</span>
                                </div>
                            </a>
                        ))}
                    </div>

                    {!loading && (
                        <button aria-label="Next" className="slider-btn next" type="button" onClick={() => scrollToIndex(active + 1)}>
                            <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                    )}
                </div>

                {/* <div className="slider-dots" aria-hidden="true">
                    {projects.map((p, i) => (
                        <button
                            key={p.id || p.title}
                            type="button"
                            className={active === i ? "is-active" : ""}
                            onClick={() => scrollToIndex(i)}
                        ></button>
                    ))}
                </div> */}

                <div className="center mt-42">
                    <Link className="btn btn-primary" to="/portfolio">View more work</Link>
                </div>
            </div>
        </section>
    );
}
